import React, { useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import AddIcon from '@mui/icons-material/AddRounded'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/SearchRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import { useQuery, useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { GET_ADVANCED_OPTION_GROUPS } from '../apollo/advancedMenuQueries'
import {
  CREATE_ADVANCED_OPTION_GROUP,
  UPDATE_ADVANCED_OPTION_GROUP,
  DELETE_ADVANCED_OPTION_GROUP
} from '../apollo/advancedMenuMutations'
import useBulkSelection, { bulkDelete } from '../components/Common/useBulkSelection'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import DragIndicatorIcon from '@mui/icons-material/DragIndicatorRounded'
import { mw, tableCardSx, headCellSx, searchFieldSx, chipSx, pillBtnSx, pillBtnPrimarySx, pillBtnDangerSx, dialogPaperSx, sectionHeaderSx, badgeSx, numSx, iconBtnSx, iconBtnDangerSx } from './menuWorkspaceStyles'

const OptionGroupsSubTab = () => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Form state
  const [name, setName] = useState('')
  const [choices, setChoices] = useState([''])
  const [selectionType, setSelectionType] = useState('SINGLE')
  const [isRequired, setIsRequired] = useState(false)
  const [error, setError] = useState('')

  const restaurantId = localStorage.getItem('restaurantId')

  const { data, loading: queryLoading } = useQuery(GET_ADVANCED_OPTION_GROUPS, {
    variables: { restaurantId },
    skip: !restaurantId,
    fetchPolicy: 'network-only'
  })

  const refetchQueries = [{ query: GET_ADVANCED_OPTION_GROUPS, variables: { restaurantId } }]

  const [createGroup, { loading: creating }] = useMutation(CREATE_ADVANCED_OPTION_GROUP, {
    refetchQueries,
    onCompleted: () => handleCloseDialog(),
    onError: (err) => setError(err.message)
  })

  const [updateGroup, { loading: updating }] = useMutation(UPDATE_ADVANCED_OPTION_GROUP, {
    refetchQueries,
    onCompleted: () => handleCloseDialog(),
    onError: (err) => setError(err.message)
  })

  const [deleteGroup] = useMutation(DELETE_ADVANCED_OPTION_GROUP, {
    refetchQueries,
    onError: (err) => console.error('Delete error:', err)
  })

  const groups = data?.advancedOptionGroups || []

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const result = q ? groups.filter(g => g.name.toLowerCase().includes(q)) : groups
    return result.slice().sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }, [groups, searchQuery])

  const resetForm = () => {
    setName('')
    setChoices([''])
    setSelectionType('SINGLE')
    setIsRequired(false)
    setError('')
    setEditingGroup(null)
  }

  const handleOpenCreate = () => {
    resetForm()
    setDialogOpen(true)
  }

  const handleOpenEdit = (group) => {
    setEditingGroup(group)
    setName(group.name || '')
    setChoices(group.choices && group.choices.length > 0 ? [...group.choices] : [''])
    setSelectionType(group.selectionType || 'SINGLE')
    setIsRequired(group.isRequired || false)
    setError('')
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    resetForm()
  }

  const handleAddChoice = () => {
    setChoices([...choices, ''])
  }

  const handleChoiceChange = (index, value) => {
    const updated = [...choices]
    updated[index] = value
    setChoices(updated)
  }

  const handleRemoveChoice = (index) => {
    if (choices.length <= 1) return
    setChoices(choices.filter((_, i) => i !== index))
  }

  // Drag-and-drop choice ordering — the saved array order is what customers see.
  const handleChoiceDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return
    setChoices(prev => {
      const next = prev.slice()
      const [moved] = next.splice(result.source.index, 1)
      next.splice(result.destination.index, 0, moved)
      return next
    })
  }

  const handleSubmit = () => {
    setError('')
    if (!name.trim()) {
      setError(t('Group name is required'))
      return
    }
    const validChoices = choices.map(c => c.trim()).filter(Boolean)
    if (validChoices.length < 2) {
      setError(t('At least 2 choices are required'))
      return
    }

    const input = {
      name: name.trim(),
      choices: validChoices,
      selectionType,
      isRequired
    }

    if (editingGroup) {
      updateGroup({ variables: { id: editingGroup._id, input } })
    } else {
      createGroup({ variables: { restaurantId, input } })
    }
  }

  const handleDelete = (e, groupId) => {
    e.stopPropagation()
    if (window.confirm(t('Delete this option group? It will be removed from all items.'))) {
      deleteGroup({ variables: { id: groupId } })
    }
  }

  const bulk = useBulkSelection(filteredGroups.map(g => g._id))

  const handleBulkDelete = async () => {
    const ids = bulk.selectedIds
    if (ids.length === 0) return
    if (!window.confirm(t('Delete {{n}} option groups? They will be removed from all items.', { n: ids.length }))) return
    const { failed } = await bulkDelete(ids, (id) => deleteGroup({ variables: { id } }))
    bulk.clear()
    if (failed > 0) setError(t('{{n}} option groups could not be deleted.', { n: failed }))
  }

  const saving = creating || updating

  return (
    <Box>
      <Box sx={sectionHeaderSx}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: mw.color.textPrimary }}>
            {t('Option Groups')}
          </Typography>
          <Typography variant="body2" sx={{ color: mw.color.textMuted, mt: 0.75, fontSize: 14 }}>
            {t('Simple choice lists with no prices — like Spice Level, Steak Doneness, etc.')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {bulk.selectedIds.length > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleBulkDelete}
              sx={pillBtnDangerSx}
            >
              {t('Delete Selected')} ({bulk.selectedIds.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={pillBtnPrimarySx}
          >
            {t('Create Option Group')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 2.5, maxWidth: 380 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search option groups...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: mw.color.textFaint }} />
              </InputAdornment>
            )
          }}
          sx={searchFieldSx}
        />
      </Box>

      <TableContainer component={Paper} sx={tableCardSx}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: mw.color.headBg }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={bulk.allSelected}
                  indeterminate={bulk.someSelected}
                  onChange={bulk.toggleAll}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              <TableCell sx={headCellSx}>{t('Name')}</TableCell>
              <TableCell sx={headCellSx}>{t('Choices')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Selection')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Required')}</TableCell>
              <TableCell sx={headCellSx} align="center">{t('Used In')}</TableCell>
              <TableCell sx={headCellSx} align="right" width={100}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {queryLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: mw.color.textMuted }}>
                  {groups.length === 0
                    ? t('No option groups yet. Create one like "Spice Level" or "Steak Doneness".')
                    : t('No groups match your search.')}
                </TableCell>
              </TableRow>
            ) : (
              filteredGroups.map((group) => (
                <TableRow
                  key={group._id}
                  hover
                  sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                  onClick={() => handleOpenEdit(group)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={bulk.isSelected(group._id)}
                      onChange={() => bulk.toggle(group._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight={500} sx={{ color: mw.color.textPrimary, fontWeight: 700, fontSize: 15 }}>
                      {group.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {group.choices.slice(0, 5).map((choice, idx) => (
                        <Chip
                          key={idx}
                          label={choice}
                          size="small"
                          sx={chipSx.option}
                        />
                      ))}
                      {group.choices.length > 5 && (
                        <Chip
                          label={`+${group.choices.length - 5}`}
                          size="small"
                          sx={chipSx.overflow}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', height: 28, px: 1.25, border: `1px solid ${mw.color.border}`, borderRadius: mw.radius.badge, bgcolor: mw.color.cardBg, color: mw.color.textSecondary, fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                      {group.selectionType === 'SINGLE' ? t('Pick One') : t('Pick Multiple')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {group.isRequired ? (
                      <Chip label={t('Required')} size="small" sx={badgeSx.required} />
                    ) : (
                      <Chip label={t('Optional')} size="small" sx={badgeSx.optional} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: mw.color.textMuted, fontSize: 14.5, whiteSpace: 'nowrap' }}>
                      <Box component="span" sx={{ ...numSx, color: mw.color.textPrimary }}>{group.itemCount ?? 0}</Box> {t('items')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap', pl: 1 }}>
                    <Tooltip title={t('Edit')}>
                      <IconButton size="small" onClick={() => handleOpenEdit(group)} sx={iconBtnSx}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('Delete')}>
                      <IconButton size="small" color="error" onClick={(e) => handleDelete(e, group._id)} sx={iconBtnDangerSx}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: mw.color.textPrimary }}>
          {editingGroup ? t('Edit Option Group') : t('Create Option Group')}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            autoFocus
            fullWidth
            label={t('Group Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            placeholder={t('e.g., Spice Level, Steak Doneness, Egg Style')}
          />

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: mw.color.textPrimary, mb: 1 }}>
              {t('Choices')}
            </Typography>
            <DragDropContext onDragEnd={handleChoiceDragEnd}>
              <Droppable droppableId="option-choices">
                {(dropProvided) => (
                  <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                    {choices.map((choice, idx) => (
                      <Draggable key={`choice-${idx}`} draggableId={`choice-${idx}`} index={idx}>
                        {(dragProvided, dragSnapshot) => (
                          <Box
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', bgcolor: dragSnapshot.isDragging ? mw.color.accentSoft : 'transparent', borderRadius: mw.radius.xs, boxShadow: dragSnapshot.isDragging ? mw.shadow.pop : 'none' }}
                          >
                            <Box {...dragProvided.dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', color: mw.color.textFaint }}>
                              <DragIndicatorIcon fontSize="small" />
                            </Box>
                            <TextField
                              fullWidth
                              size="small"
                              value={choice}
                              onChange={(e) => handleChoiceChange(idx, e.target.value)}
                              placeholder={t('e.g., Mild, Medium, Hot')}
                            />
                            {choices.length > 1 && (
                              <IconButton size="small" onClick={() => handleRemoveChoice(idx)} sx={iconBtnDangerSx}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {dropProvided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddChoice}
              sx={{ textTransform: 'none', fontWeight: 600, color: mw.color.accent }}
            >
              {t('Add Choice')}
            </Button>
          </Box>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>{t('Selection Type')}</InputLabel>
            <Select
              value={selectionType}
              onChange={(e) => setSelectionType(e.target.value)}
              label={t('Selection Type')}
            >
              <MenuItem value="SINGLE">{t('Pick One (radio buttons)')}</MenuItem>
              <MenuItem value="MULTI">{t('Pick Multiple (checkboxes)')}</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                color="primary"
              />
            }
            label={t('Required — customer must select')}
            sx={{ mt: 1, ml: 0, gap: 1, color: mw.color.textSecondary }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving} sx={pillBtnSx}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ ...pillBtnPrimarySx, minWidth: 96 }}>
            {saving ? <CircularProgress size={20} /> : editingGroup ? t('Save') : t('Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OptionGroupsSubTab
